import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [pump, setPump] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setPump(data.pump);
    } catch {
      setUser(null);
      setPump(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Google Auth (fragment contains session_id), let AuthCallback handle it.
    // Skip /auth/me so we don't race the cookie set.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (phone, password) => {
    const { data } = await api.post("/auth/login", { phone, password });
    setUser(data.user);
    await refresh();
    return data.user;
  };

  const register = async ({ name, phone, password, pump_name, omc, company_name, invite_code, referral_code }) => {
    const { data } = await api.post("/auth/register", { name, phone, password, pump_name, omc, company_name, invite_code, referral_code });
    setUser(data.user);
    setPump(data.pump);
    return data;
  };

  const acceptInvite = async ({ code, password }) => {
    const { data } = await api.post("/invites/accept", { code, password });
    setUser(data.user);
    await refresh();
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setUser(null);
    setPump(null);
  };

  return (
    <AuthContext.Provider value={{ user, pump, loading, refresh, login, register, acceptInvite, logout, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
